import {
  Box,
  Button,
  HStack,
  Spacer,
  Stack,
  Tooltip,
  useDisclosure,
} from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { MessageReactions } from 'apps/TenantApp/components/ChatApp/components/ChatMenu/components/ChatThread/components/MessageReactions';
import FeatureContainer from 'components/feature_container';
import { UserAvatar } from 'components/UserAvatar';
import { UserHandle } from 'components/UserHandle';
import useCurrentTime from 'hooks/useCurrentTime';
import { orderBy } from 'natural-orderby';
import React, { lazy, useEffect, useRef, useState } from 'react';
import { fetchQuery, useRelayEnvironment } from 'utils/graphClient';
import { RemirrorJSON } from 'remirror';
import { graphql, useFragment } from 'utils/graphClient';
import { useI18n } from 'utils/i18n';
import { notEmpty } from 'utils/NotEmptyFilter';
import { useFeatureFlag } from 'utils/sessionProvider';
import { AnnouncementMessageActions } from './components/AnnouncementMessageActions';
import {
  CommentsThread_chat_socialThread$data,
  CommentsThread_chat_socialThread$key,
} from './__generated__/CommentsThread_chat_socialThread.graphql';
import {
  CommentsThread_membersNodes_Query,
  CommentsThread_membersNodes_Query$data,
} from './__generated__/CommentsThread_membersNodes_Query.graphql';
import { EditAnnouncementMessage } from './components/EditAnnouncementMessage';

const RemirrorRenderer = lazy(() => import('components/Editor/Renderer'));

const announcementSocialThreadFragment = graphql`
  fragment CommentsThread_chat_socialThread on SocialThread {
    id
    reference
    participantsIds
    comments(first: 1000) @connection(key: "CommentsThread_comments") {
      edges {
        node {
          id
          type
          senderId
          text
          createdAt
          deletedAt
          isEdited
          updatedAt
          ...MessageReactions_chat_messageFragment
          ...ReactionButton_chat_messageFragment
          ...EditAnnouncementMessage_chat_messageFragment
          ...AnnouncementMessageActions_chat_messageFragment
        }
      }
    }
  }
`;

const membersNodesQuery = graphql`
  query CommentsThread_membersNodes_Query($ids: [ID!]!) {
    nodes(ids: $ids) {
      ... on Member {
        id
        name
        email
        ...UserHandle_member
        ...UserAvatar_member
      }
    }
  }
`;

interface CommentsThreadProps {
  socialThread: CommentsThread_chat_socialThread$key;
  newCommentsIds: string[];
}

const MAX_INIT_VISIBLE = 5;
const ONE_MINUTE = 1000 * 60;

export const CommentsThread: React.FC<CommentsThreadProps> = ({
  socialThread,
}) => {
  const { isOpen: isShowingMore, onToggle: onShowMoreToggle } = useDisclosure();
  const { participantsIds, comments } = useFragment(
    announcementSocialThreadFragment,
    socialThread,
  );

  // keep the IDs instead of the full object, then link the full object afterwards
  // this ensures we don't miss updates
  const initialCommentsRefId = useRef<string[]>([]);

  const [hasMoreMessages, setHasMoreMessages] = useState(false);

  const notDeletedNodes = comments.edges.filter(
    ({ node }) => node.deletedAt === null,
  );
  const orderedMessages = orderBy(
    notDeletedNodes,
    ({ node }) => node.createdAt,
  );
  const newComments = orderedMessages.slice(
    initialCommentsRefId.current.length,
  );

  const environment = useRelayEnvironment();

  const [members, setMembers] = useState<
    NonNullable<CommentsThread_membersNodes_Query$data['nodes'][number]>[]
  >([]);

  useEffect(() => {
    fetchQuery<CommentsThread_membersNodes_Query>(
      environment,
      membersNodesQuery,
      {
        ids: participantsIds,
      },
      { fetchPolicy: 'network-only' },
    ).subscribe({
      next: (response) => {
        setMembers(response.nodes.filter(notEmpty));
      },
    });
  }, [participantsIds.join(',')]);

  useEffect(() => {
    if (initialCommentsRefId.current.length === 0) {
      initialCommentsRefId.current = [
        ...orderedMessages.map(({ node }) => node.id),
      ];
    }

    if (initialCommentsRefId.current.length > MAX_INIT_VISIBLE + 1) {
      setHasMoreMessages(true);
    }
  }, [orderedMessages.map(({ node }) => node.id)]);

  const initialCommentsRefFilled = initialCommentsRefId.current
    .map((id) => {
      return orderedMessages.find(({ node }) => node.id === id) || null;
    })
    .filter(notEmpty);

  const displayedComments = isShowingMore
    ? orderedMessages
    : [...initialCommentsRefFilled.slice(-MAX_INIT_VISIBLE), ...newComments];

  return (
    <Stack mb={initialCommentsRefId.current.length ? 2 : 0}>
      {hasMoreMessages && !isShowingMore && (
        <Button
          variant="ghost"
          onClick={onShowMoreToggle}
          size="sm"
          width="100%"
        >
          {t`Show more comments`}
        </Button>
      )}

      {displayedComments.map(({ node: message }) => {
        return <Comment key={message.id} members={members} message={message} />;
      })}
    </Stack>
  );
};

interface CommentProps {
  message: NonNullable<
    CommentsThread_chat_socialThread$data['comments']['edges'][number]['node']
  >;
  members: NonNullable<
    CommentsThread_membersNodes_Query$data['nodes'][number]
  >[];
}

const Comment = ({ members, message }: CommentProps) => {
  const { formatDistance } = useI18n();
  const { now } = useCurrentTime({ refresh: ONE_MINUTE });
  const [isEditingMessage, setIsEditingMessage] = useState(false);

  const allowReactions = useFeatureFlag(
    'FEATURE_ALLOW_REACTIONS_ON_ANNOUNCEMENT_COMMENTS',
  );
  const allowDeletionOrModification = useFeatureFlag(
    'FEATURE_ALLOW_EDITING_AND_REMOVING_ANNOUNCEMENT_COMMENTS',
  );

  const sender = members.find(({ id }) => id === message.senderId);

  return (
    <HStack w="100%" key={message.id} alignItems="flex-start" rounded="md">
      {sender && <UserAvatar member={sender} size="xs" opacity={1} mt={1} />}

      <Stack w="100%" spacing={1}>
        <HStack>
          {sender && <UserHandle member={sender} linkToProfile />}
          <Box
            as="time"
            fontSize="xs"
            color="gray.500"
            dateTime={new Date(message.createdAt).toISOString()}
          >
            {formatDistance(message.createdAt, now, {
              addSuffix: true,
              ensurePast: true,
            })}
          </Box>
          <FeatureContainer name="FEATURE_ALLOW_EDITING_AND_REMOVING_ANNOUNCEMENT_COMMENTS">
            {message.isEdited && !isEditingMessage ? (
              <Tooltip
                label={formatDistance(new Date(message.updatedAt), new Date(), {
                  addSuffix: true,
                })}
                hasArrow
                placement="right"
              >
                <Box
                  as="span"
                  fontSize="xs"
                  color="gray.500"
                  cursor="help"
                >{t`(edited)`}</Box>
              </Tooltip>
            ) : null}
          </FeatureContainer>
          <Spacer />
          <AnnouncementMessageActions
            message={message}
            keepScrollAtTheBottom={() => {}}
            setIsEditingMessage={(value) => {
              setIsEditingMessage(value);
            }}
            allowReactions={allowReactions}
            allowDeletionOrModification={allowDeletionOrModification}
          />
        </HStack>

        {isEditingMessage ? (
          <EditAnnouncementMessage
            message={message}
            setIsEditingMessage={setIsEditingMessage}
          />
        ) : (
          <Box
            bg="gray.100"
            _dark={{ bg: 'gray.600' }}
            width="fit-content"
            borderRadius="lg"
            px={2}
            py={0.5}
          >
            <RemirrorRenderer json={message.text as RemirrorJSON} />
          </Box>
        )}

        <FeatureContainer name="FEATURE_ALLOW_REACTIONS_ON_ANNOUNCEMENT_COMMENTS">
          <MessageReactions message={message} />
        </FeatureContainer>
      </Stack>
    </HStack>
  );
};
